import { STORY_SOURCE, story } from '@/content/storyBuilder'

// Everything below the header is transcribed from the five-part full-page
// capture of digitopia.co/customers/kordsa/ supplied with this request —
// "tıpatıp tastamam şekilde hazırla" — together with the four photographs
// that page uses. The film already sat in the header, which is where the
// reference puts it, so above the fold only the lede changed.
//
// The reference's own shape, part for part: the grey standfirst band, the
// wide aircraft picture under it, then two centred sections on the white page
// (About / The Challenge), then three grey panels. "Why Kordsa Chose
// Digitopia" is a centred panel of pure prose; "The Approach" is the panel
// enclosing both columns with the picture on the left, and "The Results" its
// mirror (`photoSide: 'right'` with `photoInPanel`).
//
// The closing card is not repeated as a section: content/testimonials.ts
// already holds Fatih Akar's three paragraphs keyed to this slug, and reading
// them off the capture is what confirmed the transcription — they came back
// word for word identical, down to the attribution line.
export const added = story('kordsa', 'Kordsa', {
  doc: '68ab4024-3ef9-4afb-9d83-eeb180da3f83-KordSA and Digitopia.pdf',
  video: '2EvpsQc3jTg',
  lede: [
    'KordSA leveraged the Digital Maturity Index (DMI) and the Impact Platform to not only '
    + 'measure but also accelerate its progress towards becoming a digitally advanced '
    + 'manufacturer.',
  ],
  intro: [
    'Kordsa, a global leader in advanced material technologies and a subsidiary of Sabancı '
    + 'Holding, has been at the forefront of innovation for decades. With operations spanning '
    + 'across four continents and seven countries (Turkey, the US, Brazil, Indonesia, and '
    + 'Thailand), Kordsa identified the need for a structured digital transformation strategy.',
    'Partnering with Digitopia, KordSA leveraged the Digital Maturity Index (DMI) and the '
    + 'Impact Platform to not only measure but also accelerate its progress towards becoming a '
    + 'digitally advanced manufacturer. Kordsa has successfully leveraged data-driven insights '
    + 'to enhance efficiency, streamline operations, and future-proof its business.',
  ],
  photo: {
    src: '/photo/kordsa-aircraft-landing-gear.webp',
    alt: 'The underside of an airliner on approach, its landing gear and composite fairings in '
      + 'view against a blue sky',
  },
  sections: [
    {
      heading: 'About Kordsa: A Global Force in Reinforcement Technologies',
      centred: true,
      body: [
        'Since its inception in 1973, Kordsa has been a key player in the reinforcement '
        + 'sector, supplying top tire manufacturers and expanding into composites and '
        + 'construction. With a workforce of over 4,500 employees and two R&D centers, Kordsa '
        + 'continues to pioneer sustainable solutions in the industry. Its innovations serve '
        + 'aerospace, defense, automotive, and infrastructure sectors, reinforcing life '
        + 'through cutting-edge materials and digital innovation.',
      ],
    },
    {
      heading: 'The Challenge: Scaling Digital Maturity Across a Global Network',
      centred: true,
      body: [
        'As Kordsa expanded, it faced challenges in unifying its digital strategy across '
        + 'multiple regions. Operating with five different ERP systems, the company struggled '
        + 'with data consistency, operational alignment, and efficiency optimization. A '
        + 'fragmented digital ecosystem hindered strategic decision-making, leading Kordsa to '
        + 'seek a comprehensive solution. The need for a unified overview of technology '
        + 'operations and a shared digital transformation vision was critical. Moreover, '
        + 'staying ahead in a competitive B2B environment demanded heightened customer '
        + 'centricity and agility, coupled with the adoption of advanced technologies like AI.',
      ],
    },
    {
      heading: 'Why Kordsa Chose Digitopia: A Data-Driven Approach to Transformation',
      band: true,
      centred: true,
      body: [
        'Recognizing the importance of structured digital transformation, Kordsa selected '
        + 'Digitopia’s Digital Maturity Index (DMI) to assess, benchmark, and enhance its '
        + 'digital strategy. Additionally, Digitopia’s expertise in tailored digital roadmap '
        + 'creation provided Kordsa with a clear, customized path for transformation. The need '
        + 'for a holistic view of its digital capabilities, actionable insights, and a '
        + 'standardized approach to transformation drove this partnership. The ability to '
        + 'align with other Sabancı Holding companies using DMI further strengthened Kordsa’s '
        + 'decision.',
      ],
    },
    {
      heading: 'The Approach: A Data-Driven Transformation Roadmap',
      band: true,
      photo: {
        src: '/photo/kordsa-reinforcement-sectors-collage.webp',
        alt: 'A composite of the sectors Kordsa reinforces: an airliner, a mining truck, a '
          + 'car on a highway and a tyre being built from cord fabric',
      },
      photoSide: 'left',
      body: [
        'Kordsa embarked on a journey to consolidate its digital infrastructure. By '
        + 'implementing Digitopia’s Impact Platform and leveraging DMI assessments, the '
        + 'company gained a clear view of its digital strengths and gaps. The approach '
        + 'involved:',
      ],
      items: [
        {
          lead: 'Standardizing ERP Systems:',
          text: 'Unifying operations under one ERP system through Project Genius.',
        },
        {
          lead: 'Enhancing Data Collection & Analytics:',
          text: 'Transitioning from fragmented data points to a centralized, real-time '
            + 'analytics framework.',
        },
        {
          lead: 'Fostering AI-Driven Decision Making:',
          text: 'Implementing predictive analytics and automation to drive efficiency.',
        },
        {
          lead: 'Aligning Global Teams:',
          text: 'Bringing all stakeholders together under a shared digital vision through '
            + 'structured roadmaps.',
        },
        {
          lead: 'Tailored Digital Roadmap Creation:',
          text: 'Ensuring a customized strategy for each region and business unit to optimize '
            + 'digital investments.',
        },
      ],
    },
    {
      heading: 'The Results: A Unified Digital Strategy with Tangible Impact',
      band: true,
      photo: {
        src: '/photo/kordsa-global-headquarters.webp',
        alt: 'The Kordsa Global building on the Sabancı University campus, glass-fronted '
          + 'against a blue sky',
      },
      photoSide: 'right',
      photoInPanel: true,
      body: [
        'Holistic Digital Maturity Assessments: Conducted evaluations across all global sites, '
        + 'aligning strategies company-wide.',
      ],
      items: [
        {
          lead: 'Stronger Executive Buy-in:',
          text: 'Demonstrated tangible digital impact, securing top management support.',
        },
        {
          lead: 'Global Benchmarking:',
          text: 'Positioned itself as one of the most digitally mature companies within '
            + 'Sabancı Holding.',
        },
        {
          lead: 'Operational Efficiency:',
          text: 'Enhanced visibility and oversight across global operations have driven '
            + 'considerable improvements in operational efficiency.',
        },
        {
          lead: 'Cost and Time Savings:',
          text: 'Strategic decision-making, underpinned by DMI’s insights, has resulted in '
            + 'substantial savings, optimizing resource allocation.',
        },
        {
          lead: 'Strategic Alignment:',
          text: 'The establishment of a cohesive digital vision and the alignment of '
            + 'initiatives across global operations have streamlined KordSA’s journey towards '
            + 'digital excellence.',
        },
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/kordsa/ supplied with the request, not from a file we hold, so please `
    + 'proofread the figures against the source before production — “inception in 1973”, “over '
    + '4,500 employees”, “two R&D centers”, “five different ERP systems”, and the country list, '
    + 'which the reference introduces as “four continents and seven countries” and then names '
    + 'five of (Turkey, the US, Brazil, Indonesia, Thailand). The company’s own name is set two '
    + 'ways on the reference and both are kept exactly as they appear: “Kordsa” in the prose '
    + 'and “KordSA” in the lede and the closing result. One reassurance: the three paragraphs '
    + 'of Fatih Akar’s quote read off this capture match the ones already in '
    + 'content/testimonials.ts word for word, so the transcription is not guesswork. Two things '
    + 'to look at rather than decide. The picture columns of the Approach and Results panels '
    + 'are both blank in the capture, and the sector collage and the Kordsa Global building are '
    + 'placed in them on the assumption that they are what did not load — swap them if that is '
    + 'wrong. And a fourth photograph came with the request, the lunar lander beside a splashed-'
    + 'down capsule, which the reference gives no place at all; it was left out rather than put '
    + 'somewhere the capture does not show it, so say where it belongs and it is one line. '
    + 'Lastly the two panel photographs are small originals — 599px and 1100px wide, which is '
    + 'what the live site serves — so they will look soft on a high-density screen until larger '
    + 'files arrive. One deliberate template-wide difference from the reference: the header '
    + 'eyebrow reads “SUCCESS STORIES” on digitopia.co, which is not this page’s own line.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 12
